import { useCallback, useState } from 'react'

import { useApolloClient } from '@apollo/client/react'
import type { ErrorLike, FetchPolicy } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { MentionData_Mention } from '~publish/pages/Community/Mentions/fragments/mentionData'
import type { MentionData } from '~publish/pages/Community/Mentions/fragments/mentionData'

/**
 * Fetches a single mention's full conversation for the details dialog: the
 * mention itself, its thread-root post, ancestors, and descendants (replies).
 *
 * Every branch selects `...MentionData_Mention`, the shape the card tree
 * consumes through `MentionContext` — so the dialog can feed the mention to
 * `MentionThreadContent`, the post to `MentionPostMiniature`, and each
 * ancestor to `MentionContextItem` without reshaping.
 *
 * The inbox card's own on-expand fetch (`useMentionConversation`) selects only
 * `post` + `ancestors`; this query adds `engagement` + `descendants` because
 * the dialog lands on a bare id and must reconstruct the whole thread.
 */
export const MentionThreadQueryGQL = graphql(
  /* GraphQL */ `
    query MentionThread($input: EngagementConversationByIdInput!) {
      engagementConversationById(input: $input) {
        engagement {
          __typename
          ... on Mention {
            ...MentionData_Mention
          }
        }
        post {
          __typename
          ... on Mention {
            ...MentionData_Mention
          }
        }
        # Single page per side: the preview shows the immediate thread, the
        # same 20-item cap the inbox conversation fetch uses. Deep threads that
        # exceed it are a follow-up (wire first/after into the dialog). all so
        # the preview shows everyone's replies, not just the account's own.
        ancestors(first: 20) {
          edges {
            node {
              __typename
              ... on Mention {
                ...MentionData_Mention
              }
            }
          }
        }
        descendants(first: 20, ownershipScope: all) {
          edges {
            node {
              __typename
              ... on Mention {
                ...MentionData_Mention
              }
            }
          }
        }
      }
    }
  `,
  [MentionData_Mention],
)

type MentionThreadQueryVariables = VariablesOf<typeof MentionThreadQueryGQL>

type MentionThreadConversation = ResultOf<
  typeof MentionThreadQueryGQL
>['engagementConversationById']

/**
 * A polymorphic `Engagement` node — narrowed to `Mention` at the boundary. The
 * four selections carry different union members (only `post` and `engagement`
 * are nullable), so the helpers accept all of them and narrow by `__typename`.
 */
type EngagementNode =
  | MentionThreadConversation['engagement']
  | MentionThreadConversation['post']
  | MentionThreadConversation['ancestors']['edges'][number]['node']
  | MentionThreadConversation['descendants']['edges'][number]['node']

export type MentionThread = {
  /** The mention the dialog was opened for. Null when not found. */
  mention: MentionData | null
  /** Thread-root post (a `mentionType: post` mention). Null when absent. */
  post: MentionData | null
  /** Ancestor thread items, oldest-first (direct parent closest to the mention). */
  context: MentionData[]
  /** Replies following the mention, oldest-first. */
  replies: MentionData[]
}

export type UseMentionThreadResult = {
  getMentionThread: (
    mentionId: string,
    options?: { fetchPolicy?: FetchPolicy },
  ) => Promise<MentionThread>
  loading: boolean
  error: ErrorLike | null
}

/** Narrow a polymorphic engagement node onto `Mention`. */
const asMention = (node: EngagementNode): MentionData | null =>
  node?.__typename === 'Mention' ? node : null

const toMentions = (
  edges: ReadonlyArray<{ node: EngagementNode }>,
): MentionData[] =>
  edges
    .map((edge) => asMention(edge.node))
    .filter((mention): mention is MentionData => mention !== null)

export const useMentionThread = (): UseMentionThreadResult => {
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorLike | null>(null)

  // `useApolloClient().query()` (not `useLazyQuery`) so each call can pick its
  // own `fetchPolicy` — lazy-query exec options don't accept one.
  const getMentionThread = useCallback(
    async (
      mentionId: string,
      options?: { fetchPolicy?: FetchPolicy },
    ): Promise<MentionThread> => {
      setLoading(true)
      setError(null)

      try {
        const { data } = await client.query<
          ResultOf<typeof MentionThreadQueryGQL>,
          MentionThreadQueryVariables
        >({
          query: MentionThreadQueryGQL,
          variables: {
            input: { engagementId: mentionId, engagementType: 'mention' },
          },
          fetchPolicy: options?.fetchPolicy,
        })

        const conversation = data?.engagementConversationById

        // Ancestors arrive newest-first (closest to the mention). Reverse to
        // chronological order so the direct parent sits closest to the main
        // mention — matching the inbox card's context section.
        const context = conversation
          ? toMentions(conversation.ancestors.edges).toReversed()
          : []

        // Descendants arrive newest-first; the card tree renders replies
        // oldest-first (newest closest to the reply form), so reverse them too.
        const replies = conversation
          ? toMentions(conversation.descendants.edges).toReversed()
          : []

        return {
          mention: asMention(conversation?.engagement ?? null),
          post: asMention(conversation?.post ?? null),
          context,
          replies,
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'))
        throw err
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  return {
    getMentionThread,
    loading,
    error,
  }
}
